import type { Client, FetchResponse, MaybeOptionalInit } from "openapi-fetch";
import type { HttpMethod, MediaType, PathsWithMethod, RequiredKeysOf } from "openapi-typescript-helpers";

import { MutationException } from "./http.exception";
import type { RequestTimeout } from "./http.type";

/**
 * Mirrors openapi-fetch's own `InitParam` (not exported): init stays optional until the operation
 * requires params, and the index signature is added after inference so that check still happens.
 * `timeout` is ours — openapi-fetch copies it onto the Request for `timeoutMiddleware`.
 */
type InitParam<Init> =
  RequiredKeysOf<Init> extends never
    ? [(Init & { timeout?: RequestTimeout; [key: string]: unknown })?]
    : [Init & { timeout?: RequestTimeout; [key: string]: unknown }];

/**
 * Indexes an operation without `TPaths[P][M]`, which TS cannot resolve while `TPaths` is generic.
 * The second conditional only restates the constraint `FetchResponse` declares, so the operation
 * type reaches it unchanged and its 2XX payload stays narrowable.
 */
type OperationOf<TPaths, P extends keyof TPaths, M extends HttpMethod> = TPaths[P] extends {
  [K in M]: infer Operation;
}
  ? Operation extends Record<string | number, unknown>
    ? Operation
    : never
  : never;

/** The 2XX branch of openapi-fetch's result union. */
type SuccessResult<TPaths, P extends keyof TPaths, M extends HttpMethod, Init> = Extract<
  FetchResponse<OperationOf<TPaths, P, M>, Init, MediaType>,
  { error?: never }
>;

/**
 * Wraps `client.request` so a non-2xx response throws instead of being returned as `{ error }`.
 * Callers get `data` already narrowed to the success payload.
 */
export const createSafeRequest =
  <TPaths extends object>(client: Client<TPaths>) =>
  async <
    M extends HttpMethod,
    P extends PathsWithMethod<TPaths, M>,
    Init extends MaybeOptionalInit<TPaths[P] & Record<M, unknown>, M>,
  >(
    method: M,
    path: P,
    ...init: InitParam<Init>
  ): Promise<SuccessResult<TPaths, P, M, Init>> => {
    // `request` only type-checks against a concrete `paths`; the cast is confined here so that every
    // call site keeps its inferred params and payload.
    const request = client.request as (
      method: M,
      path: P,
      ...init: InitParam<Init>
    ) => Promise<FetchResponse<OperationOf<TPaths, P, M>, Init, MediaType>>;

    const result = await request(method, path, ...init);

    if (result.error) {
      throw new MutationException(
        { method, url: path as string, status: result.response.status, args: JSON.stringify(init[0]) },
        JSON.stringify(result.error),
      );
    }

    // The union cannot be narrowed while `TPaths` is generic; the guard above has already ruled the
    // error branch out.
    return result as SuccessResult<TPaths, P, M, Init>;
  };

export type SafeRequest<TPaths extends object> = ReturnType<typeof createSafeRequest<TPaths>>;
